/**
 * Epic 15, E15-US2 — čistý create script pro CELÝ aktuální model (jako by šel
 * do prázdné databáze), ne diff proti baseline. Stejný celkový vzor jako
 * `ChangeScriptPanel.tsx`/`SyncPanel.tsx` (sebeobsažené tlačítko + modál),
 * ale bez pre-flightu/scanování — generuje se přímo z aktuálního `schema`
 * (server si model stejně přečte znovu, `createScriptService.ts`), žádná
 * baseline se nečte ani neposouvá.
 */
import { useState } from 'react';
import { Copy, Download, FilePlus2, X } from 'lucide-react';
import type { SchemaModel } from '@bits2schema/shared';
import { api } from '../api/client';
import { Modal } from '../components/Modal';
import { SqlPreview } from '../components/SqlPreview';
import { useT } from '../i18n';
import { describeError } from '../utils/format';

interface CreateScriptPanelProps {
  projectId: string;
  schema: SchemaModel;
}

type Phase =
  | { kind: 'form' }
  | { kind: 'generating' }
  | { kind: 'result'; script: string; statementCount: number }
  | { kind: 'error'; message: string };

export function CreateScriptPanel({ projectId, schema }: CreateScriptPanelProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: 'form' });
  // Vlastní název cílového schématu je nabízený jen u jednoschématových
  // projektů (epic-15 kap. 5/9) — u víceschématových se pole vůbec nezobrazí,
  // script se vygeneruje s původními názvy schémat beze změny.
  const distinctSchemas = [...new Set(schema.tables.map((table) => table.schema))];
  const [schemaName, setSchemaName] = useState('');

  const openPanel = () => {
    setSchemaName(distinctSchemas.length === 1 ? distinctSchemas[0] : '');
    setPhase({ kind: 'form' });
    setOpen(true);
  };

  const closePanel = () => {
    setOpen(false);
    setPhase({ kind: 'form' });
  };

  const generate = async () => {
    setPhase({ kind: 'generating' });
    try {
      const trimmed = schemaName.trim();
      const overrideName =
        distinctSchemas.length === 1 && trimmed && trimmed !== distinctSchemas[0] ? trimmed : undefined;
      const result = await api.generateCreateScript(projectId, overrideName);
      setPhase({ kind: 'result', script: result.script, statementCount: result.statementCount });
    } catch (caught) {
      setPhase({ kind: 'error', message: describeError(caught, t) });
    }
  };

  const downloadScript = (script: string) => {
    const blob = new Blob([script], { type: 'application/sql' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'create-script.sql';
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyScript = async (script: string) => {
    await navigator.clipboard.writeText(script);
  };

  return (
    <>
      <button type="button" onClick={openPanel}>
        <FilePlus2 size={16} />
        {t('createScript.button')}
      </button>

      {open && (
        <Modal
          title={t('createScript.title')}
          onClose={phase.kind === 'generating' ? () => {} : closePanel}
          wide
          actions={renderActions()}
        >
          <div className="stack">
            <p className="hint">{t('createScript.description')}</p>

            {phase.kind === 'form' && distinctSchemas.length === 1 && (
              <div className="field">
                <label htmlFor="create-script-schema-name">{t('createScript.schemaNameLabel')}</label>
                <input
                  id="create-script-schema-name"
                  value={schemaName}
                  onChange={(event) => setSchemaName(event.target.value)}
                />
                <p className="hint">{t('createScript.schemaNameHint')}</p>
              </div>
            )}

            {phase.kind === 'generating' && <p className="hint">{t('createScript.generating')}</p>}

            {phase.kind === 'result' &&
              (phase.statementCount === 0 ? (
                <div className="notice">{t('createScript.empty')}</div>
              ) : (
                <div className="stack">
                  <div className="notice notice--success">
                    {t('createScript.result.summary', { count: phase.statementCount })}
                  </div>
                  <SqlPreview sql={phase.script} />
                </div>
              ))}

            {phase.kind === 'error' && <div className="notice notice--error">{phase.message}</div>}
          </div>
        </Modal>
      )}
    </>
  );

  function renderActions() {
    switch (phase.kind) {
      case 'form':
        return (
          <>
            <button type="button" onClick={closePanel}>
              <X size={16} />
              {t('common.cancel')}
            </button>
            <button type="button" className="primary" onClick={() => void generate()}>
              <FilePlus2 size={16} />
              {t('createScript.generateButton')}
            </button>
          </>
        );
      case 'result':
        return (
          <>
            <button type="button" onClick={() => void copyScript(phase.script)}>
              <Copy size={16} />
              {t('createScript.result.copy')}
            </button>
            <button type="button" onClick={() => downloadScript(phase.script)}>
              <Download size={16} />
              {t('createScript.result.download')}
            </button>
            <button type="button" className="primary" onClick={closePanel}>
              <X size={16} />
              {t('common.close')}
            </button>
          </>
        );
      case 'error':
        return (
          <button type="button" className="primary" onClick={closePanel}>
            <X size={16} />
            {t('common.close')}
          </button>
        );
      default:
        return (
          <button type="button" onClick={closePanel}>
            <X size={16} />
            {t('common.cancel')}
          </button>
        );
    }
  }
}
